import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "./Footer";
import {
  ArrowRight,
  BadgeDollarSign,
  CheckCircle2,
  ChevronDown,
  Globe,
  Plane,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Star,
  Users,
  Wallet,
  Zap,
  ArrowUp,
} from "lucide-react";
import CreditCardMock from "./CreditCardMock";
import ConnectWalletModal from "@/components/ConnectWalletModal";
import { trackMetaPixelIssueCardClick } from "@/lib/metaPixel";
import heroWallet from "@/assets/hero-wallet.avif";
import metalCardBlue from "@/assets/metal-card-blue.png";
import fullTrustWalletLogo from "@/assets/full-trust-wallet-logo.webp";
import { useLanguage } from "@/i18n/LanguageContext";
import { Language, languageNames, languageFlags } from "@/i18n/translations";

const walletLogos = [
  { src: "/trust-assets/wallets/metamask-logo-name.svg", alt: "MetaMask" },
  { src: "/trust-assets/wallets/trust-logo-name.png", alt: "Trust Wallet" },
  { src: "/trust-assets/wallets/coinbase-logo-name.png", alt: "Coinbase" },
  { src: "/trust-assets/wallets/phantom-logo-name.svg", alt: "Phantom" },
  { src: "/trust-assets/wallets/ledger-logo-name.svg", alt: "Ledger" },
  { src: "/trust-assets/wallets/exodus-logo-name.png", alt: "Exodus" },
  { src: "/trust-assets/wallets/electrum-logo-name.png", alt: "Electrum" },
  { src: "/trust-assets/wallets/atomic-logo-name.png", alt: "Atomic Wallet" },
  { src: "/trust-assets/wallets/tronlink-logo-name-dark.png", alt: "TronLink" },
];

const backers = [
  { src: "/trust-assets/backers/a16z-crypto-dark.svg", alt: "a16z crypto" },
  { src: "/trust-assets/backers/paradigm-dark.svg", alt: "Paradigm" },
  { src: "/trust-assets/backers/sequoia-dark.svg", alt: "Sequoia" },
  { src: "/trust-assets/backers/pantera.svg", alt: "Pantera" },
  { src: "/trust-assets/backers/blockchaincapital-dark.svg", alt: "Blockchain Capital" },
];

const premiumBenefits = [
  { icon: Star, labelKey: "premium.benefit1" },
  { icon: Plane, labelKey: "premium.benefit2" },
  { icon: Users, labelKey: "premium.benefit3" },
  { icon: Sparkles, labelKey: "premium.benefit4" },
];

const StatCard = ({ icon: Icon, title, subtitle, className = "" }: { icon: typeof Sparkles; title: string; subtitle: string; className?: string }) => (
  <div className={`rounded-[1.25rem] border border-border/60 bg-card/95 p-3 shadow-xl shadow-foreground/5 backdrop-blur lg:rounded-[1.5rem] lg:p-5 ${className}`}>
    <div className="flex items-center gap-2 lg:gap-3">
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary text-primary lg:h-10 lg:w-10">
        <Icon className="h-4 w-4 lg:h-5 lg:w-5" />
      </div>
      <div>
        <div className="text-lg font-extrabold tracking-tight text-foreground lg:text-2xl">{title}</div>
        <div className="text-xs text-muted-foreground lg:text-sm">{subtitle}</div>
      </div>
    </div>
  </div>
);

const LanguageSelector = () => {
  const { language, setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const allLangs: Language[] = ["en", "ru", "ar", "de", "ko", "tr", "ja", "zh", "pt", "es", "fr", "it", "pl"];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 rounded-xl border border-border/60 bg-card/80 px-2 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-secondary sm:gap-1.5 sm:px-3 sm:py-2 sm:text-sm"
      >
        <span className="text-sm sm:text-base">{languageFlags[language]}</span>
        <span className="hidden sm:inline">{languageNames[language]}</span>
        <ChevronDown className="h-3 w-3 text-muted-foreground sm:h-3.5 sm:w-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-48 rounded-xl border border-border/60 bg-card py-2 shadow-xl">
            {allLangs.map((lang) => (
              <button
                key={lang}
                onClick={() => { setLanguage(lang); setOpen(false); }}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-secondary ${
                  lang === language ? "text-primary font-semibold" : "text-foreground"
                }`}
              >
                <span className="text-lg">{languageFlags[lang]}</span>
                <span>{languageNames[lang]}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const ScrollToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!visible) return null;

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="fixed bottom-6 right-6 z-50 flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:-translate-y-0.5"
      aria-label="Scroll to top"
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
};

const TrustLanding = () => {
  const marqueeLogos = [...walletLogos, ...walletLogos];
  const { t } = useLanguage();
  const [walletOpen, setWalletOpen] = useState(false);

  const featureKeys = [
    { icon: Wallet, titleKey: "features.1.title", descKey: "features.1.desc" },
    { icon: Globe, titleKey: "features.2.title", descKey: "features.2.desc" },
    { icon: Smartphone, titleKey: "features.3.title", descKey: "features.3.desc" },
    { icon: ShieldCheck, titleKey: "features.4.title", descKey: "features.4.desc" },
    { icon: Zap, titleKey: "features.5.title", descKey: "features.5.desc" },
    { icon: BadgeDollarSign, titleKey: "features.6.title", descKey: "features.6.desc" },
  ];

  const heroPerks = [t("hero.perk1"), t("hero.perk2"), t("hero.perk3")];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/90 backdrop-blur-xl">
        <div className="container flex h-20 items-center justify-between relative">
          <a href="#top" className="flex shrink-0 items-center" aria-label="Trust Wallet homepage">
            <img src={fullTrustWalletLogo} alt="Trust Wallet" className="h-8 w-auto md:h-[2.7rem]" />
          </a>

          <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-10 text-base text-muted-foreground lg:flex">
            <a href="#features" className="transition-colors hover:text-foreground">{t("nav.features")}</a>
            <a href="#rewards" className="transition-colors hover:text-foreground">{t("nav.rewards")}</a>
            <a href="#premium" className="transition-colors hover:text-foreground">{t("nav.premium")}</a>
            <Link to="/faq" className="transition-colors hover:text-foreground">{t("nav.faq")}</Link>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <LanguageSelector />
            <button
              onClick={() => { trackMetaPixelIssueCardClick("landing-cta"); setWalletOpen(true); }}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 md:gap-2 md:rounded-2xl md:px-6 md:py-3 md:text-sm"
            >
              {t("nav.getStarted")}
              <ArrowRight className="h-3.5 w-3.5 md:h-4 md:w-4" />
            </button>
          </div>
        </div>
      </header>

      <main id="top" className="overflow-hidden">
        <section className="relative border-b border-border/50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.07)_0,transparent_58%)]" />
          <div
            className="absolute inset-0 opacity-70"
            style={{
              backgroundImage: "radial-gradient(circle, hsl(var(--border)) 1px, transparent 1px)",
              backgroundSize: "32px 32px",
            }}
          />
          <div className="gradient-orb left-[10%] top-20 h-72 w-72 bg-[hsl(var(--primary)/0.12)]" />
          <div className="gradient-orb right-[6%] top-24 h-80 w-80 bg-[hsl(var(--primary-light)/0.14)]" />

          <div className="container relative grid gap-6 pb-6 pt-6 lg:gap-16 lg:pb-32 lg:pt-20 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="max-w-3xl text-center lg:text-left">
              <div className="animate-fade-in-up inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card/80 px-4 py-1.5 text-sm font-semibold text-primary shadow-lg shadow-primary/10 backdrop-blur lg:px-5 lg:py-2 lg:text-base">
                <Sparkles className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                {t("hero.badge")}
              </div>

              <h1 className="animate-fade-in-up-delay-1 mt-4 font-bold leading-[1.1] tracking-[-0.03em] text-foreground lg:mt-7 lg:font-semibold lg:leading-[1.1] lg:tracking-[-0.04em]" style={{ fontSize: 'clamp(2.5rem, 8.5vw, 4.2rem)' }}>
                <span className="whitespace-pre-line">{t("hero.title1")}</span>
                <br />
                <span className="text-foreground lg:text-primary">{t("hero.title2")}</span>
              </h1>

              <p className="animate-fade-in-up-delay-2 mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-xl lg:mx-0 lg:mt-8 lg:text-xl">
                {t("hero.desc")}
              </p>

              <div className="animate-fade-in-up-delay-3 mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center lg:mt-10 lg:gap-4 lg:justify-start">
                <button
                  onClick={() => { trackMetaPixelIssueCardClick("landing-cta"); setWalletOpen(true); }}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3 text-base font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5 lg:px-8 lg:py-4 lg:text-lg"
                >
                  {t("hero.issueCard")}
                  <ArrowRight className="h-5 w-5" />
                </button>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center rounded-2xl border border-border bg-card px-6 py-3 text-base font-semibold text-foreground transition-colors hover:bg-secondary lg:px-8 lg:py-4 lg:text-lg"
                >
                  {t("hero.learnMore")}
                </a>
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground sm:text-lg lg:mt-10 lg:gap-x-6 lg:justify-start">
                {heroPerks.map((perk) => (
                  <div key={perk} className="flex items-center gap-1.5 lg:gap-2.5">
                    <CheckCircle2 className="h-4 w-4 text-success lg:h-5 lg:w-5" />
                    <span>{perk}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative mt-8 flex items-center justify-center lg:mt-0">
              <img
                src={heroWallet}
                alt="Trust Wallet app with crypto balance and security keys"
                className="w-full max-w-[520px] lg:max-w-none drop-shadow-[0_30px_60px_hsl(var(--primary)/0.25)] select-none"
                draggable={false}
              />
            </div>
          </div>
        </section>

        <section className="border-b border-border/50 py-8">
          <div className="container flex flex-col items-center gap-6">
            <div className="flex items-center gap-4">
              <img src="/trust-assets/wallets/apple-pay.svg" alt="Apple Pay" className="h-12 w-auto opacity-80" loading="lazy" />
              <img src="/trust-assets/wallets/google-pay.svg" alt="Google Pay" className="h-12 w-auto opacity-80" loading="lazy" />
            </div>
            <p className="text-center text-lg text-muted-foreground">{t("wallets.title")}</p>
          </div>
          <div className="mt-8 overflow-hidden">
            <div className="animate-marquee flex w-max items-center gap-16 px-8" style={{ animationDelay: '-12.5s' }}>
              {[...marqueeLogos, ...marqueeLogos].map((logo, index) => (
                <img key={`${logo.alt}-${index}`} src={logo.src} alt={logo.alt} className="h-10 w-auto shrink-0 opacity-45 grayscale transition-opacity duration-300 hover:opacity-80" />
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-border/50 py-20">
          <div className="container">
            <p className="text-center text-xl text-muted-foreground">{t("backers.title")}</p>
            <div className="mt-10 grid grid-cols-2 items-center gap-10 opacity-80 sm:grid-cols-3 lg:grid-cols-5">
              {backers.map((logo) => (
                <div key={logo.alt} className="flex items-center justify-center rounded-2xl border border-border/50 bg-card/60 px-6 py-6">
                  <img src={logo.src} alt={logo.alt} className="h-10 w-auto object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="border-b border-border/50 py-24">
          <div className="container">
            <div className="mx-auto max-w-3xl text-center">
              <p className="text-lg font-semibold text-primary">{t("features.label")}</p>
              <h2 className="mt-4 text-4xl font-semibold tracking-[-0.04em] text-foreground sm:text-5xl">{t("features.title")}</h2>
              <p className="mt-5 text-xl leading-relaxed text-muted-foreground">{t("features.desc")}</p>
            </div>
            <div className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {featureKeys.map((feature) => {
                const Icon = feature.icon;
                return (
                  <article key={feature.titleKey} className="rounded-[2rem] border border-border/60 bg-card/80 p-8 shadow-xl shadow-foreground/5 backdrop-blur transition-transform duration-300 hover:-translate-y-1">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-primary">
                      <Icon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-6 text-2xl font-bold tracking-tight text-foreground">{t(feature.titleKey)}</h3>
                    <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{t(feature.descKey)}</p>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section id="rewards" className="border-b border-border/50 bg-secondary/40 py-24">
          <div className="container grid gap-14 lg:grid-cols-[1fr_1fr] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/20 bg-card/80 px-4 py-1.5 text-sm font-semibold text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                {t("rewards.label")}
              </div>
              <h2 className="mt-5 text-4xl font-semibold tracking-[-0.04em] text-foreground sm:text-5xl">{t("rewards.title")}</h2>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">{t("rewards.desc")}</p>
              <div className="mt-10 space-y-6">
                {[
                  { value: "3%", labelKey: "rewards.dining", width: "100%" },
                  { value: "2%", labelKey: "rewards.shopping", width: "66%" },
                  { value: "1%", labelKey: "rewards.other", width: "33%" },
                ].map((item) => (
                  <div key={item.labelKey}>
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-2xl font-semibold text-primary">{item.value}</span>
                      <span className="text-base text-muted-foreground">{t(item.labelKey)}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-border">
                      <div className="h-full rounded-full bg-primary" style={{ width: item.width }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative flex items-center justify-center">
              <div className="grid w-full max-w-md grid-cols-2 gap-5">
                <div className="rounded-[1.75rem] border border-border/60 bg-card/90 p-6 shadow-lg shadow-foreground/5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#FF060A]/10">
                    <img src="/trust-assets/crypto/tron.svg" alt="Tron" className="h-7 w-7" />
                  </div>
                  <div className="mt-3 text-lg font-bold text-foreground">Tron</div>
                  <div className="text-sm text-muted-foreground">Up to 3% back</div>
                </div>
                <div className="rounded-[1.75rem] border border-border/60 bg-card/90 p-6 shadow-lg shadow-foreground/5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#627EEA]/10">
                    <img src="/trust-assets/crypto/ethereum.svg" alt="Ethereum" className="h-7 w-7" />
                  </div>
                  <div className="mt-3 text-lg font-bold text-foreground">Ethereum</div>
                  <div className="text-sm text-muted-foreground">Up to 3% back</div>
                </div>

                <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                  <div className="flex h-20 w-20 flex-col items-center justify-center rounded-full bg-primary text-primary-foreground shadow-[var(--shadow-button)] ring-4 ring-primary/20">
                    <Sparkles className="mb-0.5 h-3.5 w-3.5" />
                    <span className="text-lg font-semibold leading-none">5%</span>
                    <span className="text-[0.5rem] font-bold uppercase tracking-[0.2em]">Max</span>
                  </div>
                </div>

                <div className="rounded-[1.75rem] border border-border/60 bg-card/90 p-6 shadow-lg shadow-foreground/5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#F3BA2F]/10">
                    <img src="/trust-assets/crypto/bsc.svg" alt="BSC" className="h-7 w-7" />
                  </div>
                  <div className="mt-3 text-lg font-bold text-foreground">BSC</div>
                  <div className="text-sm text-muted-foreground">Up to 2% back</div>
                </div>
                <div className="rounded-[1.75rem] border border-border/60 bg-card/90 p-6 shadow-lg shadow-foreground/5">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#8247E5]/10">
                    <img src="/trust-assets/crypto/polygon.svg" alt="Polygon" className="h-7 w-7" />
                  </div>
                  <div className="mt-3 text-lg font-bold text-foreground">Polygon</div>
                  <div className="text-sm text-muted-foreground">Up to 4% back</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="premium" className="relative py-24">
          <div className="container relative">
            <div className="rounded-[2.5rem] border border-border/60 bg-card/90 px-8 py-14 sm:px-14 lg:px-16 lg:py-16 shadow-lg shadow-foreground/5">
              <div className="grid gap-12 lg:grid-cols-[1fr_1fr] lg:items-center">
                <div>
                  <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
                    <Star className="h-3.5 w-3.5 fill-primary" />
                    {t("premium.badge")}
                  </div>
                  <h2 className="mt-6 text-4xl font-semibold tracking-[-0.04em] text-foreground sm:text-5xl">
                    {t("premium.title1")}<br />{t("premium.title2")}
                  </h2>
                  <p className="mt-6 max-w-lg text-lg leading-relaxed text-muted-foreground">{t("premium.desc")}</p>
                  <div className="mt-10 grid gap-4 sm:grid-cols-2">
                    {premiumBenefits.map((benefit) => {
                      const Icon = benefit.icon;
                      return (
                        <div key={benefit.labelKey} className="flex items-center gap-3">
                          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 shrink-0">
                            <Icon className="h-4.5 w-4.5 text-primary" />
                          </div>
                          <span className="text-base font-medium text-foreground">{t(benefit.labelKey)}</span>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => { trackMetaPixelIssueCardClick("landing-cta"); setWalletOpen(true); }}
                    className="mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-7 py-4 text-lg font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5 sm:w-auto"
                  >
                    {t("premium.cta")}
                    <ArrowRight className="h-5 w-5" />
                  </button>
                </div>
                <div className="mx-auto w-full max-w-[500px]">
                  <img
                    src={metalCardBlue}
                    alt="Trust Wallet metal card"
                    className="w-full h-auto rounded-[1.5rem]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="cta" className="border-t border-border/50 bg-card/50 py-24">
          <div className="container">
            <div className="mx-auto max-w-4xl rounded-[2.5rem] border border-border/60 bg-background/90 px-8 py-14 text-center shadow-2xl shadow-foreground/5 backdrop-blur sm:px-14">
              <h2 className="text-4xl font-semibold tracking-[-0.04em] text-foreground sm:text-5xl">{t("cta.title")}</h2>
              <p className="mx-auto mt-5 max-w-2xl text-xl leading-relaxed text-muted-foreground">{t("cta.desc")}</p>
              <button
                onClick={() => { trackMetaPixelIssueCardClick("landing-cta"); setWalletOpen(true); }}
                className="mt-10 inline-flex items-center gap-2 rounded-2xl bg-primary px-8 py-4 text-lg font-semibold text-primary-foreground shadow-[var(--shadow-button)] transition-transform hover:-translate-y-0.5"
              >
                {t("cta.button")}
                <ArrowRight className="h-5 w-5" />
              </button>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4 text-base text-muted-foreground">
                {[t("cta.tag1"), t("cta.tag2"), t("cta.tag3")].map((item) => (
                  <div key={item} className="rounded-full border border-border/60 bg-card px-5 py-2.5">{item}</div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTopButton />
      <ConnectWalletModal open={walletOpen} onOpenChange={setWalletOpen} />
    </div>
  );
};

export default TrustLanding;
